import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["data-dropdown-id"]
const _hoisted_2 = ["innerHTML", "onClick"]
const _hoisted_3 = {
  key: 0,
  class: "search-li"
}
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["data-menu-id"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { key: 0 }
const _hoisted_9 = ["src", "alt"]
const _hoisted_10 = ["innerHTML"]
const _hoisted_11 = ["data-menu-id"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = ["src", "alt"]
const _hoisted_15 = ["innerHTML"]

import { computed, ref, nextTick, onMounted, onUnmounted } from 'vue';

type TItem = {
  value: string | number;
  text: string | number;
  icon?: string;
  items?: TItem[];
};

interface TProps {
  select: string | string[];
  placeholder?: string;
  multiple?: boolean;
  items: TItem[];
  scrollable?: boolean; // Добавляем прокрутку для больших списков
  customSelectStyle?: boolean; // Кастомные стили для selected
  searchable?: boolean; // Включить поиск по элементам
}
interface TEmit {
  (e: 'update:select', value: string | string[]): void;
}

type TMenu = {
  visible: boolean;
};

type TSubmenu = {
  [name: string]: TMenu;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'UiSelect',
  props: {
    select: null,
    placeholder: null,
    multiple: { type: Boolean },
    items: null,
    scrollable: { type: Boolean },
    customSelectStyle: { type: Boolean },
    searchable: { type: Boolean }
  },
  emits: ["update:select"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'update:select', value: string | string[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  select: string | string[];
  placeholder?: string;
  multiple?: boolean;
  items: TItem[];
  scrollable?: boolean; // Добавляем прокрутку для больших списков
  customSelectStyle?: boolean; // Кастомные стили для selected
  searchable?: boolean; // Включить поиск по элементам
}





const dropdownId = ref('dropdown-' + Math.random().toString(36).substr(2, 9));

// Обработчик кликов для закрытия всех остальных UiSelect при клике на любой
function handleGlobalClick(event: MouseEvent) {
  const target = event.target as HTMLElement;
  
  // Находим ближайший UiSelect, по которому кликнули, и его ID
  const clickedDropdownId = target.closest('.dropdown')?.getAttribute('data-dropdown-id');
  
  // Если клик был не по этому UiSelect - закрываем это меню
  if (clickedDropdownId !== dropdownId.value) {
    menu.value.visible = false;
    searchQuery.value = '';
  }
}

onMounted(() => {
  // Используем фазу захвата (capture), чтобы обработчик срабатывал раньше
  document.addEventListener('click', handleGlobalClick, true);
});

onUnmounted(() => {
  document.removeEventListener('click', handleGlobalClick, true);
});

const selected = computed({
  get() {
    return props.select;
  },
  set(value) {
    emit('update:select', value);
  },
});

const menu = ref<TMenu>({ visible: false });
const subMenu = ref<TSubmenu>({});
const subSubMenu = ref<TSubmenu>({});
const searchQuery = ref(''); // Строка поиска

// Фильтрация элементов по поиску
const filteredItems = computed(() => {
  if (!props.searchable || !searchQuery.value) {
    return props.items;
  }
  const query = searchQuery.value.toLowerCase();
  
  const filterItems = (items: TItem[]): TItem[] => {
    return items.filter(item => {
      // Проверяем, содержит ли текст элемента поисковый запрос
      const matchesSearch = item.text.toLowerCase().includes(query);
      // Если есть вложенные элементы, фильтруем их рекурсивно
      if (item.items) {
        const filteredSubItems = filterItems(item.items);
        if (filteredSubItems.length > 0) {
          return true;
        }
      }
      return matchesSearch;
    }).map(item => {
      // Если есть вложенные элементы, возвращаем их отфильтрованную версию
      if (item.items) {
        return {
          ...item,
          items: filterItems(item.items)
        };
      }
      return item;
    });
  };
  
  return filterItems(props.items);
});

const isSelectionArray = computed(
  () => props.multiple && typeof selected.value !== 'string',
);

const selectedString = computed(() => {
  if (isSelectionArray.value) {
    const getIncludeValueInSelected = (items: TItem[]) => {
      const result: any[] = [];

      for (const opt of items) {
        if (opt.items) {
          const selectedItems = getIncludeValueInSelected(opt.items);
          for (const item of selectedItems) {
            result.push(item);
          }
        }

        if (selected.value.includes(opt.value.toString())) {
          result.push(opt);
        }
      }

      return result;
    };

    const result = getIncludeValueInSelected(props.items)
      .map((opt) => opt.text)
      .join(', ');

    return result;
  } else {
    return props.items.find((opt) => opt.value === selected.value)?.text || '';
  }
});

function selectItem(value: string | number) {
  if (isSelectionArray.value) {
    const index = selected.value.indexOf(value.toString());
    const selectedArray = selected.value as string[];
    if (index === -1) {
      selectedArray.push(value.toString());
    } else {
      selectedArray.splice(index, 1);
    }
  } else {
    selected.value = value.toString();
    toggleMenu();
  }
}

function toggleMenu() {
  menu.value.visible = !menu.value.visible;
  // Сбрасываем поиск при закрытии меню
  if (!menu.value.visible) {
    searchQuery.value = '';
  }
}
function toggleSubmenu(name: string) {
  if (!(name in subMenu.value)) {
    subMenu.value[name] = {
      visible: false,
    };
  }

  for (const sName in subMenu.value) {
    if (sName === name) continue;
    subMenu.value[sName].visible = false;
  }

  subMenu.value[name].visible = !subMenu.value[name].visible;

  setMenuSize(`submenu-${name}`);
}
function toggleSubSubmenu(name: string) {
  if (!(name in subSubMenu.value)) {
    subSubMenu.value[name] = {
      visible: false,
    };
  }

  for (const sName in subSubMenu.value) {
    if (sName === name) continue;
    subSubMenu.value[sName].visible = false;
  }

  subSubMenu.value[name].visible = !subSubMenu.value[name].visible;

  setMenuSize(`subSubMenu-${name}`);
}

async function setMenuSize(menuId: string): Promise<void> {
  await nextTick();

  const menu = document.querySelector<HTMLElement>(
    `[data-menu-id="${menuId}"]`,
  );

  if (menu) {
    const { clientHeight, clientWidth } = document.documentElement;
    const { x, y, width, height } = menu.getBoundingClientRect();

    if (x + width > clientWidth) {
      menu.style.left = `calc(100% - 2px - ${width}px)`;
      menu.style.top =
        parseFloat(menu.style.top ? menu.style.top : '0') + 30 + 'px';
    }
    if (y + height > clientHeight) {
      menu.style.top = -1 * (clientHeight - height) + 'px';
    }
  }
}

// Экспортируем метод для закрытия меню извне
expose({
  closeMenu: () => {
    menu.value.visible = false;
  },
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("nav", {
    class: _normalizeClass(["dropdown", { 'custom-select-style': props.customSelectStyle }]),
    "data-dropdown-id": dropdownId.value
  }, [
    _createElementVNode("div", {
      class: "selected",
      innerHTML: _unref(selectedString) || props.placeholder || '',
      onClick: _withModifiers(toggleMenu, ["stop"])
    }, null, 8, _hoisted_2),
    _createElementVNode("ul", {
      class: _normalizeClass([{ open: menu.value.visible }, { scrollable: props.scrollable }])
    }, [
      (props.searchable)
        ? (_openBlock(), _createElementBlock("li", _hoisted_3, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              class: "search-input",
              placeholder: "Поиск..."
            }, null, 512), [
              [_vModelText, searchQuery.value]
            ])
          ]))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredItems), (item) => {
        return (_openBlock(), _createElementBlock("li", {
          key: item.value.toString(),
          class: _normalizeClass([
          { dropdown: item.items, arrow: item.items },
          {
            'selected-item': _unref(isSelectionArray)
              ? _unref(selected).includes(item.value.toString())
              : _unref(selected) === item.value,
          },
        ]),
          onClick: _withModifiers(($event: any) => (
          item.items
            ? toggleSubmenu(item.value.toString())
            : selectItem(item.value)
        ), ["stop"])
        }, [
          _createElementVNode("div", {
            innerHTML: item.text.toString()
          }, null, 8, _hoisted_5),
          (item.items)
            ? (_openBlock(), _createElementBlock("ul", {
                key: 0,
                class: _normalizeClass(["submenu", [{ open: subMenu.value[item.value]?.visible }]]),
                "data-menu-id": `submenu-${item.value}`,
                "aria-label": "submenu"
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.items, (subitem) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: subitem.value.toString(),
                    class: _normalizeClass([
              'd-flex',
              'position-relative',
              { dropdown: subitem.items, arrow: subitem.items },
              {
                'selected-item': _unref(isSelectionArray)
                  ? _unref(selected).includes(subitem.value.toString())
                  : _unref(selected) === subitem.value,
              },
            ]),
                    onClick: _withModifiers(($event: any) => (
              subitem.items
                ? toggleSubSubmenu(subitem.value.toString())
                : selectItem(subitem.value)
            ), ["stop"])
                  }, [
                    (subitem.icon)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                          _createElementVNode("img", {
                            width: "25",
                            height: "25",
                            style: {"margin-right":"2px"},
                            src: subitem.icon,
                            alt: subitem.value + ''
                          }, null, 8, _hoisted_9)
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", {
                      innerHTML: subitem.text.toString()
                    }, null, 8, _hoisted_10),
                    (subitem.items)
                      ? (_openBlock(), _createElementBlock("ul", {
                          key: 1,
                          class: _normalizeClass(["submenu", [{ open: subSubMenu.value[subitem.value]?.visible }]]),
                          "data-menu-id": `subSubMenu-${subitem.value}`,
                          "aria-label": "submenu"
                        }, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(subitem.items, (subSubItem) => {
                            return (_openBlock(), _createElementBlock("li", {
                              key: subSubItem.value.toString(),
                              class: _normalizeClass([
                  'd-flex',
                  {
                    'selected-item': _unref(isSelectionArray)
                      ? _unref(selected).includes(subSubItem.value.toString())
                      : _unref(selected) === subSubItem.value,
                  },
                ]),
                              onClick: _withModifiers(($event: any) => (selectItem(subSubItem.value)), ["stop"])
                            }, [
                              (subitem.icon)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                    _createElementVNode("img", {
                                      width: "25",
                                      height: "25",
                                      style: {"margin-right":"2px"},
                                      src: subSubItem.icon,
                                      alt: subSubItem.value + ''
                                    }, null, 8, _hoisted_14)
                                  ]))
                                : _createCommentVNode("", true),
                              _createElementVNode("div", {
                                innerHTML: subSubItem.text.toString()
                              }, null, 8, _hoisted_15)
                            ], 10, _hoisted_12))
                          }), 128))
                        ], 10, _hoisted_11))
                      : _createCommentVNode("", true)
                  ], 10, _hoisted_7))
                }), 128))
              ], 10, _hoisted_6))
            : _createCommentVNode("", true)
        ], 10, _hoisted_4))
      }), 128))
    ], 2)
  ], 10, _hoisted_1))
}
}

})